// @givmo/assistant-panel — the page → panel insertion seam.
//
// The panel PROPOSES and the person DECIDES (D-EP-38/D8): the only thing a reply may do to the page
// behind it is drop its text into a field the person then edits and saves themselves. Nothing here
// writes to a server, and there is deliberately no way for it to.
//
// ══ WHY A REGISTRY RATHER THAN A PROP ══════════════════════════════════════════════════════════
//
// The panel lives in the app SHELL and the field lives inside a page, several routes down. Threading
// a callback between them would mean every route in the host carrying an assistant prop it mostly
// ignores. So the page announces itself the same way the shell's entry point does — a module-level
// registration read through `useSyncExternalStore` — and the panel subscribes. A page that has no
// field registers nothing, and the panel then offers Copy alone: the draft is still there to use,
// there is simply nowhere on THIS page to put it, which is the honest state rather than a control
// that does nothing.
//
// ⚠ ONE TARGET AT A TIME, LAST REGISTRATION WINS, and each registration returns its own
// unregistration. Two fields claiming the panel at once has no correct answer — "insert" would have
// to pick — so the seam refuses to model it. The returned function is idempotent and only clears a
// registration that is still the current one, which is what makes it safe under React's
// mount-effect ordering (a new page's effect can run before the old page's cleanup, and a cleanup
// that cleared unconditionally would take the incoming page's target down with it).

import { useSyncExternalStore } from 'react';

/** A field on the page that can receive a proposed draft. */
export interface AssistantInsertionTarget {
  /**
   * What the control offers to do, in the host's own words ("Insert into description"). Host-supplied
   * because only the page knows what its field is called.
   */
  actionLabel: string;
  /** Write `text` into the field. The person edits and saves; this saves nothing. */
  insert: (text: string) => void;
}

let target: AssistantInsertionTarget | null = null;
const subscribers = new Set<() => void>();

function notifyAll(): void {
  for (const notify of subscribers) notify();
}

/**
 * Announce this page's insertion target. Returns the unregistration — call it on unmount.
 *
 * The unregistration clears only if THIS registration is still current, so a later page that
 * registered first is never torn down by an earlier page's cleanup.
 */
export function registerAssistantInsertionTarget(next: AssistantInsertionTarget): () => void {
  target = next;
  notifyAll();
  return () => {
    if (target !== next) return;
    target = null;
    notifyAll();
  };
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

function getSnapshot(): AssistantInsertionTarget | null {
  return target;
}

/** The page's current insertion target, or `null` when no page is offering one. */
export function useAssistantInsertionTarget(): AssistantInsertionTarget | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Clear the registry. For tests — production code unregisters through the returned function. */
export function resetAssistantInsertionTarget(): void {
  target = null;
  notifyAll();
}
